import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { HorizontalContainer } from '@picsart/design-system/components/HorizontalContainer';

import useStyles from './styles';
import usePageLanguage from '../../hooks/usePageLanguage';
import useFontsDataStore from '../../stores/fontsDataStore';
import BannerContainer from '../Banner/BannerContainer';
import CategoryList from './CategoryList';
import FilterSelect from './FilterSelect';
import CustomBreadCrumbs from './CustomBreadCrumbs';
import TopBanner from './TopBanner';
import SearchInput from '../SearchInput';
import FontsList from './FontsList';
import { getLandingsByCategory } from '../../services/Fonts';
import { BANNER_IMAGE, INPUT_PLACEHOLDER } from './constants';
import useQueryParams from './hooks/useQueryParam';
import usePageDataStore from '../../stores/pageDataStore';
import useUpdateMetaData from './hooks/useUpdateMetaData';
import { filterPrice, filterStyle } from './data';
import { FontsType, LandingsType } from './types';

function Fonts({ title, description }: FontsType) {
  const styles = useStyles();
  const navigate = useNavigate();
  const { language } = usePageLanguage();
  const [landings, setLandings] = useState<LandingsType[]>();
  const { page } = useParams();
  const [searchParams] = useSearchParams();
  const { queryData, isPaid } = useQueryParams();
  const { fontsData, fetchFontsData, searchFonts, isLoading } = useFontsDataStore(state => ({
    fetchFontsData: state.fetchFontsData,
    fontsData: state.fontsData,
    searchFonts: state.searchFonts,
    isLoading: state.isLoading,
  }));
  const { loading } = usePageDataStore(state => ({
    loading: state.loading,
  }));
  const { updateMeta } = useUpdateMetaData();
  const chipTexts = Array.isArray(landings) ? landings.map(chip => chip.name.toLowerCase()) : [];

  useEffect(() => {
    void (async () => {
      try {
        const result = (await getLandingsByCategory(language)) as LandingsType[];
        setLandings(result);
      } catch (err) {
        console.error('Error fetching landings by Category', err);
      }
    })();
  }, [language]);

  const updateUrl = useCallback(() => {
    let updatedUrl = window.location.href;
    if (!updatedUrl.endsWith('/')) {
      updatedUrl += '/';
    }
    updatedUrl = updatedUrl.replace(/\/all\/$/, '/');

    window.history.replaceState(null, '', updatedUrl);
  }, []);

  const fetchData = useCallback(
    async (reset = true) => {
      try {
        const searchParam = queryData || page;

        if (searchParam || isPaid !== null) {
          updateUrl();
          await searchFonts(searchParam, isPaid, reset);
        } else {
          await fetchFontsData();
        }
        updateMeta();
      } catch (err) {
        console.error('Error fetching fonts data:', err);
      }
    },
    [fetchFontsData, page, isPaid, queryData, searchFonts],
  );

  const loadFonts = useCallback(async () => {
    await (fontsData.isFilter ? fetchData(false) : fetchFontsData());
  }, [fetchData, fetchFontsData, fontsData.isFilter]);

  useEffect(() => {
    if (searchParams.get('q') && searchParams.get('q').length > 0) {
      const categoryFont = landings?.find(
        item => item.slug.toLowerCase() === `fonts/${searchParams.get('q').toLowerCase()}`,
      );
      if (categoryFont) {
        navigate(`/${categoryFont.slug}/`);
      } else {
        console.error('Font not found');
      }
    }
  }, [landings, navigate, searchParams]);

  useEffect(() => {
    void (async () => {
      try {
        await fetchData();
      } catch (err) {
        window.location.href = '/not-found';
      }
    })();
  }, [fetchData]);

  return (
    <div className={styles.root}>
      <BannerContainer className={styles.headerBg} backgroundImage={BANNER_IMAGE}>
        <HorizontalContainer>
          <CustomBreadCrumbs landings={landings} />
        </HorizontalContainer>
        <div className={styles.bannerContainer}>
          <TopBanner title={title} description={description} isLoading={loading} />
          <SearchInput placeholder={INPUT_PLACEHOLDER} containerClassName={styles.searchInput} />
          <CategoryList landings={landings} pagePath={page} />
        </div>
        <HorizontalContainer>
          <div className={styles.filters}>
            <FilterSelect filterData={filterPrice} />
            <FilterSelect filterData={filterStyle} />
          </div>
        </HorizontalContainer>
      </BannerContainer>
      <HorizontalContainer>
        <FontsList
          isLoading={isLoading}
          fontsData={fontsData}
          chipTexts={chipTexts}
          fetchData={loadFonts}
        />
      </HorizontalContainer>
    </div>
  );
}

export default Fonts;
